'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="We Scored 132 Products for Microplastics. Only 12 Have Ever Been Measured."
      author="MicroPlastics Research Desk"
      date="August 17, 2026"
      readTime="9 min read"
      heroImage="/blog/heroes/blog-research-desk.webp"
      heroImageAlt="A research desk with laboratory glassware beside a screen showing a product database"
    >
      <BlogLastUpdated iso="2026-08-17" formatted="August 17, 2026" />

      <BlogTLDR>
        <strong>Nine percent of the product scores on this site rest on someone
        having measured that exact item. The other 91% are reasoned.</strong>{' '}
        We publish an evidence grade on every product page, but we had never
        reported the distribution across all of them. Here it is: 12 products
        at grade A, 29 at B, 70 at C, 21 at D. The more uncomfortable finding
        is that the measured products score substantially worse than the
        reasoned ones — and that gap survives when you remove the measurement
        itself from the arithmetic.
      </BlogTLDR>

      <p>
        Every product page on this site carries a number from 0 to 100, an
        evidence grade from A to D, and a confidence percentage. The grade is
        the part almost nobody reads, and it is the part that decides what the
        number is worth. So we audited our own database and counted.
      </p>

      <h2>What 132 products actually rest on</h2>

      <p>
        Grade A means a peer-reviewed measurement exists for that exact product
        or an item identical to it. Grade D means we are reasoning from the
        polymer and the way the thing is used, with no direct study of anything
        close to it.
      </p>

      <BlogComparisonTable
        caption="Evidence grades across all 132 scored products, August 2026"
        columns={[
          { key: 'grade', label: 'Grade' },
          { key: 'n', label: 'Products' },
          { key: 'share', label: 'Share' },
          { key: 'meaning', label: 'What it means' },
        ]}
        rows={[
          { grade: 'A', n: '12', share: '9%', meaning: 'This exact item, or one identical to it, has been measured' },
          { grade: 'B', n: '29', share: '21%', meaning: 'A close analogue has been measured' },
          { grade: 'C', n: '70', share: '53%', meaning: 'Reasoned from polymer behaviour and use conditions' },
          { grade: 'D', n: '21', share: '15%', meaning: 'Reasoned from material class alone' },
        ]}
      />

      <p>
        A majority of the database — 53% — sits at grade C. If you have ever
        looked up a product here and taken the number at face value, the honest
        framing is that you were most likely reading a well-argued estimate
        rather than a laboratory result. That is what the grade has always been
        there to tell you. We just had not added it up before.
      </p>

      <p>
        The measured 9% is not evenly spread, either. Five of the twelve grade-A
        products are bottled waters, which is a direct consequence of the{' '}
        <Link href="/blog/microplastics-bottled-water-brands-ranked" className="text-forest underline">2018 Mason study and the 2024 PNAS work</Link>{' '}
        having concentrated on that one category. Personal care, coffee, food
        storage, drinkware, household and water filters have <em>no</em> grade-A
        product at all between them — 56 products, not one of them measured
        directly.
      </p>

      <h2>The finding we did not expect</h2>

      <p>
        Measured products score worse. Grade-A items average 62.3 out of 100.
        Grade-C items average 39.9. That is a 22-point gap, and it points the
        opposite way from the comfortable assumption that testing mostly
        reassures.
      </p>

      <p>
        The obvious objection is that this is circular. One of the five scoring
        components is <em>measured release</em>, worth up to 25 points, and by
        construction it can only score high when a measurement exists. So we
        removed it and recalculated:
      </p>

      <BlogComparisonTable
        caption="Mean score by evidence grade, with and without the measurement component"
        columns={[
          { key: 'grade', label: 'Grade' },
          { key: 'mean', label: 'Mean score' },
          { key: 'meas', label: 'Measurement component' },
          { key: 'excl', label: 'Score excluding measurement' },
        ]}
        rows={[
          { grade: 'A', mean: '62.3', meas: '20.8', excl: '41.6' },
          { grade: 'B', mean: '45.8', meas: '13.4', excl: '32.4' },
          { grade: 'C', mean: '39.9', meas: '6.9', excl: '33.0' },
          { grade: 'D', mean: '40.6', meas: '5.6', excl: '35.0' },
        ]}
      />

      <p>
        The gap narrows but does not close. Strip the measurement out entirely
        and grade-A products still score 41.6 against 32 to 35 for everything
        else. It shows up in components that are scored independently of whether
        a study exists: packaging contact averages 18.6 for grade-A items versus
        14.0 to 15.6 elsewhere, and use conditions 15.9 versus 11.8 to 13.8.
      </p>

      <h2>Why that is selection bias, not a discovery</h2>

      <p>
        The tempting read is &ldquo;measurement reveals exposure that reasoning
        misses.&rdquo; We do not think that is what this shows, and it would be
        a convenient conclusion for a company that sells a scanner, so it is
        worth being careful.
      </p>

      <p>
        The likelier explanation is that <strong>researchers measure what they
        already suspect is worst</strong>. Nobody funds a study of a wooden
        cutting board. They fund studies of{' '}
        <Link href="/blog/microplastics-in-tea-bags" className="text-forest underline">pyramid tea bags steeped at 95°C</Link>,{' '}
        of{' '}
        <Link href="/blog/best-baby-bottles-microplastic-free" className="text-forest underline">polypropylene baby bottles under shaking and heat</Link>,
        of bottled water that has sat in a warehouse. Those are already
        high-contact, high-temperature, high-agitation items before anyone
        measures them — which is exactly why they score high on packaging and
        use conditions independently of the lab result.
      </p>

      <p>
        The practical consequence matters more than the statistic. Our grade-C
        numbers are not simply blurrier versions of our grade-A numbers. They
        describe a <em>systematically different population of products</em> —
        one that skews toward lower-contact, lower-temperature items that nobody
        has bothered to test. Treating the two sets as interchangeable, in
        either direction, would be a mistake.
      </p>

      <h2>What scores worst, by category</h2>

      <p>
        Averaged across every product we have scored in each category, with the
        count of directly measured items alongside:
      </p>

      <BlogComparisonTable
        caption="Mean exposure score by category (categories with 5+ products)"
        columns={[
          { key: 'cat', label: 'Category' },
          { key: 'n', label: 'Products' },
          { key: 'mean', label: 'Mean score' },
          { key: 'a', label: 'Measured (grade A)' },
        ]}
        rows={[
          { cat: 'Textiles', n: '7', mean: '58.3', a: '2' },
          { cat: 'Packaged food', n: '22', mean: '48.8', a: '1' },
          { cat: 'Baby', n: '9', mean: '46.6', a: '1' },
          { cat: 'Tea', n: '6', mean: '44.7', a: '1' },
          { cat: 'Cookware', n: '14', mean: '44.1', a: '2' },
          { cat: 'Personal care', n: '12', mean: '42.8', a: '0' },
          { cat: 'Bottled water', n: '18', mean: '42.7', a: '5' },
          { cat: 'Food storage', n: '8', mean: '41.6', a: '0' },
          { cat: 'Coffee', n: '9', mean: '39.7', a: '0' },
          { cat: 'Household', n: '9', mean: '38.8', a: '0' },
          { cat: 'Drinkware', n: '10', mean: '35.6', a: '0' },
          { cat: 'Water filters', n: '8', mean: '32.2', a: '0' },
        ]}
      />

      <p>
        Two things stand out. Textiles top the table on a mean of 58.3 from only
        seven products, which is the smallest sample here and should be read as
        a signal to test more rather than a settled finding. And bottled water,
        the most-studied category on the list by a distance, sits mid-table at
        42.7 — below packaged food and below baby products, neither of which has
        anything like the same evidence behind it.
      </p>

      <p>
        The extremes are worth naming because they are where the reasoning is
        easiest to check. The highest score in the database is a pyramid mesh
        tea bag at 87: woven plastic, submerged, at close to boiling point, with
        nothing between the polymer and the drink. The lowest is a wooden
        cutting board at 8, followed by loose leaf tea with a steel infuser at 9
        and a stainless lunch box at 10. In every one of those cases the score
        is low for the same reason — there is no polymer in the contact path at
        all.
      </p>

      <BlogKeyTakeaways
        items={[
          'Only 12 of 132 products (9%) have a measurement of that exact item behind their score. 53% are reasoned from polymer behaviour and use conditions.',
          'Six categories — personal care, coffee, food storage, drinkware, household and water filters, 56 products in total — contain no directly measured product at all.',
          'Measured products score 22 points higher on average, and the gap survives removing the measurement component, so it is not a scoring artefact.',
          'The likeliest cause is selection bias in the underlying science: studies target items already expected to shed, which are high-contact and high-temperature to begin with.',
          'Read a grade-C score as a well-reasoned estimate that could move if someone tests it — not as a weaker version of a lab result.',
        ]}
      />

      <h2>What we are doing about it</h2>

      <p>
        The obvious response to &ldquo;most of your numbers are reasoned&rdquo;
        is to publish fewer numbers. We think that is the wrong call. A reasoned
        estimate with its reasoning shown, its confidence stated and its grade
        on the page is more useful than silence, provided nobody is misled about
        which kind of number they are reading.
      </p>

      <p>
        What it does change is where the next work goes. The six categories with
        zero measured products are where our numbers are weakest, and three of
        them — personal care, coffee and drinkware — are among the most-scanned
        categories in the app. That is the gap worth closing, and it is a
        research problem rather than a writing one. You can read the full
        scoring method, component by component, on our{' '}
        <Link href="/methodology" className="text-forest underline">methodology page</Link>.
      </p>

      <BlogAppCTA
        headline="See the grade before you trust the number"
        sub="Every product in the app carries the same evidence grade and confidence figure as the database behind this article. Scan something and check what its score actually rests on."
      />

      <BlogFAQ
        items={[
          {
            question: 'Does a grade C score mean the product is safe?',
            answer:
              'No, and it does not mean it is unsafe either. The grade describes how much evidence sits behind the number, not how the product performs. A grade-C score of 70 still describes a high-contact item; it just means the 70 was reasoned from the polymer and how the thing is used rather than read off a laboratory result.',
          },
          {
            question: 'Why not just score the 12 products you have measurements for?',
            answer:
              'Because the questions people actually ask are about the other 120. Refusing to answer would not make anyone better informed; it would just move them to a source that answers with no grade, no confidence figure and no stated method. We would rather publish an estimate and label it clearly as one.',
          },
          {
            question: 'Which categories have the weakest evidence?',
            answer:
              'Personal care, coffee, food storage, drinkware, household and water filters — 56 products between them and not one directly measured. Every score in those categories is reasoned from materials and use conditions.',
          },
          {
            question: 'Do measured products genuinely release more microplastics?',
            answer:
              'Our data cannot answer that, and we would be overreaching to claim it does. Measured products score 22 points higher here, but the most likely explanation is that researchers choose to test items they already suspect will shed — which are high-contact, high-temperature items to begin with. It is a fact about what science has studied, not a proven fact about the products.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'MicroPlastics Research Desk',
            year: 2026,
            title: 'MicroPlastics Exposure Score methodology — component weights, evidence grades and confidence',
            url: 'https://microplasticsapp.com/methodology',
          },
          {
            authors: 'Mason SA, Welch VG, Neratko J',
            year: 2018,
            title: 'Synthetic polymer contamination in bottled water. Frontiers in Chemistry 6:407',
            url: 'https://www.frontiersin.org/articles/10.3389/fchem.2018.00407/full',
          },
          {
            authors: 'Qian N, Gao X, Lang X, et al.',
            year: 2024,
            title: 'Rapid single-particle chemical imaging of nanoplastics by SRS microscopy. PNAS 121(3)',
            url: 'https://www.pnas.org/doi/10.1073/pnas.2300582121',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
